import { supabase } from './supabase/client'
import { currencies } from '../constants/currencies'

interface UserSettings {
    currency: string
    language: string
    notifications: boolean
    theme: string
    timeFormat: string
    lastUpdated: string
    createdAt: string
    preferences: {
        currency: string
        language: string
        notifications: boolean
        theme: string
    }
}

function getCurrentISOString(): string {
    return new Date().toISOString()
}

const DEFAULT_SETTINGS: UserSettings = {
    currency: "USD",
    language: "en",
    notifications: true,
    theme: "light",
    timeFormat: "12",
    lastUpdated: getCurrentISOString(),
    createdAt: getCurrentISOString(),
    preferences: {
        currency: "USD",
        language: "en",
        notifications: true,
        theme: "light"
    }
}

export async function updateUserCurrency(userId: string, newCurrency: string = 'USD'): Promise<boolean> {
    try {
        // Validate the currency code
        const isValidCurrency = currencies.some(c => c.code === newCurrency)
        if (!isValidCurrency) {
            console.error('Invalid currency code:', newCurrency)
            return false
        }

        const now = getCurrentISOString()

        // Update user settings in Supabase
        const { error } = await supabase
            .from('settings')
            .upsert({
                id: userId,
                user_id: userId,
                preferences: {
                    currency: newCurrency,
                    language: 'en',
                    theme: 'light',
                    notifications: true
                },
                default_currency: newCurrency,
                updated_at: now,
                created_at: now
            }, { onConflict: 'id' })

        if (error) {
            console.error('Error updating user currency:', error)
            return false
        }

        return true
    } catch (error) {
        console.error('Error updating user currency:', error)
        return false
    }
}

export async function fetchSettings(): Promise<UserSettings> {
    try {
        // Get current user session
        const { data: sessionData } = await supabase.auth.getSession()
        const session = sessionData?.session
        
        if (!session?.user) {
            return DEFAULT_SETTINGS
        }
        
        const userId = session.user.id

        // Fetch settings from Supabase
        const { data, error } = await supabase
            .from('settings')
            .select('*')
            .eq('id', userId)
            .single()

        if (error || !data) {
            console.error('Error fetching settings:', error)
            return DEFAULT_SETTINGS
        }

        try {
            // Try to parse existing dates or use current date
            const now = getCurrentISOString()
            const createdAt = typeof data.created_at === 'string' ? data.created_at : now

            // Create settings object with proper date handling
            return {
                ...DEFAULT_SETTINGS,
                currency: data.default_currency || DEFAULT_SETTINGS.currency,
                language: data.language || DEFAULT_SETTINGS.language,
                theme: data.theme || DEFAULT_SETTINGS.theme,
                timeFormat: data.time_format || DEFAULT_SETTINGS.timeFormat,
                preferences: {
                    ...DEFAULT_SETTINGS.preferences,
                    ...(data.preferences || {})
                },
                lastUpdated: now,
                createdAt
            }
        } catch (dateError) {
            console.error('Error processing dates:', dateError)
            return {
                ...DEFAULT_SETTINGS,
                preferences: {
                    ...DEFAULT_SETTINGS.preferences,
                    ...(data.preferences || {})
                },
                lastUpdated: getCurrentISOString(),
                createdAt: getCurrentISOString()
            }
        }
    } catch (error) {
        console.error('Error in fetchSettings:', error)
        return DEFAULT_SETTINGS
    }
} 